import axios from 'axios';
import { getToken } from './auth';
import type {
  EnhancedDashboardStats,
  DailyDashboardSummary,
  Article,
  GoogleIndexStatus,
  ArticleListParams,
  ArticleListResponse,
  TaggedItem,
} from '../models';

const API_URL_STORAGE_KEY = 'ai11_api_base_url';
const DEFAULT_API_URL = (import.meta.env.VITE_API_URL || 'http://localhost:8000/api') + '/staff';

export const STAFF_API_PRESETS = [
  { label: 'Production', url: 'https://api.ai11.in/api/staff' },
  { label: 'Staging', url: 'https://xapi.ai11.in/api/staff' },
  { label: 'Local', url: 'http://127.0.0.1:8000/api/staff' },
] as const;

/** Resolve the active API base URL (user override → env → default). */
export function getApiBaseUrl(): string {
  try {
    const stored = localStorage.getItem(API_URL_STORAGE_KEY);
    if (stored && stored.trim()) return stored.trim();
  } catch { /* ignore */ }
  return DEFAULT_API_URL;
}

/** Returns the raw override value (or empty string if using default). */
export function getApiBaseUrlOverride(): string {
  try {
    return localStorage.getItem(API_URL_STORAGE_KEY) || '';
  } catch { return ''; }
}

/** Set a custom API base URL. Pass empty string to revert to default. */
export function setApiBaseUrl(url: string): void {
  const trimmed = url.trim();
  if (trimmed) {
    localStorage.setItem(API_URL_STORAGE_KEY, trimmed);
  } else {
    localStorage.removeItem(API_URL_STORAGE_KEY);
  }
  // Update the live axios instance
  api.defaults.baseURL = getApiBaseUrl();
}

export const API_URL = getApiBaseUrl();

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include token and resolve base URL dynamically
api.interceptors.request.use(
  (config) => {
    config.baseURL = getApiBaseUrl();
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token expiration
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

interface PaginationParams {
  page?: number;
  page_size?: number;
  search?: string;
  format?: string;
  status?: string;
  series?: number;
  match_id?: number;
  is_draft?: boolean;
  squad__series?: number;
}

// Player related API calls
export const playerApi = {
  getAll: (params?: PaginationParams) => api.get('/cricket/players/', { params }),
  getById: (id: number) => api.get(`/cricket/players/${id}/`),
  create: (data: any) => api.post('/cricket/players/', data),
  update: (id: number, data: any) => api.put(`/cricket/players/${id}/`, data),
  patch: (id: number, data: any) => api.patch(`/cricket/players/${id}/`, data),
  delete: (id: number) => api.delete(`/cricket/players/${id}/`),
  refreshStats: (id: number) => api.get(`/cricket/players/${id}/refresh_stats/`),
  getBattingInnings: (playerId: number, pageSize: number = 5) => api.get('/cricket/batting_innings/', { params: { player: playerId, page_size: pageSize } }),
  getBowlingInnings: (playerId: number, pageSize: number = 5) => api.get('/cricket/bowling_innings/', { params: { player: playerId, page_size: pageSize } }),
};

// Squad related API calls
export const squadApi = {
  getAll: (params?: PaginationParams) => api.get('/cricket/squads/', { params }),
  getByMatch: (matchId: number) => api.get(`/cricket/squads/by_match/`, { params: { match_id: matchId } }),
  getById: (id: number) => api.get(`/cricket/squads/${id}/`),
};

interface MatchFilterParams extends PaginationParams {
  from_date?: string;
  to_date?: string;
  start_time_after?: string;
  start_time_before?: string;
}

// Match related API calls
export const matchApi = {
  getAll: (params?: MatchFilterParams) => api.get('/cricket/matches/', { params }),
  getById: (id: number) => api.get(`/cricket/matches/${id}/`),
  create: (data: any) => api.post('/cricket/matches/', data),
  update: (id: number, data: any) => api.put(`/cricket/matches/${id}/`, data),
  patch: (id: number, data: any) => api.patch(`/cricket/matches/${id}/`, data),
  delete: (id: number) => api.delete(`/cricket/matches/${id}/`),
  getContentBlocksSchema: () => api.get('/cricket/matches/content_blocks_schema/'),
  updateMatchTeam: (id: number, data: any) => api.patch(`/cricket/match_teams/${id}/`, data),
  aiLineup: (id: number, data: { lineup_text: string; llm_model?: string }) => api.post(`/cricket/matches/${id}/ai_lineup/`, data),
  aiPredictLineup: (id: number, llm_model?: string) => api.post(`/cricket/matches/${id}/ai_predict_lineup/`, { llm_model }),
};

// Tournament related API calls
export const seriesApi = {
  getAll: (params?: PaginationParams) => api.get('/cricket/series/', { params }),
  getById: (id: number) => api.get(`/cricket/series/${id}/`),
  create: (data: any) => api.post('/cricket/series/', data),
  update: (id: number, data: any) => api.put(`/cricket/series/${id}/`, data),
  patch: (id: number, data: any) => api.patch(`/cricket/series/${id}/`, data),
  delete: (id: number) => api.delete(`/cricket/series/${id}/`),
  createFromPlatform: (tournamentData: Record<string, string>) => api.post('/cricket/series/create_from_platform/', tournamentData),
  
  // Admin actions
  initialize: (id: number) => api.post(`/cricket/series/${id}/initialize/`),
  generateSquadStats: (id: number) => api.post(`/cricket/series/${id}/generate_squad_stats/`),
  fetchD11Matches: (id: number) => api.post(`/cricket/series/${id}/fetch_d11_matches/`),
  fetchSimilarSeries: (id: number) => api.post(`/cricket/series/${id}/fetch_similar_series/`),
};

// MatchPlayer related API calls
export const matchPlayerApi = {
  getAll: (params?: PaginationParams) => api.get('/cricket/match_players/', { params }),
  getById: (id: number) => api.get(`/cricket/match_players/${id}/`),
  create: (data: any) => api.post('/cricket/match_players/', data),
  update: (id: number, data: any) => api.put(`/cricket/match_players/${id}/`, data),
  patch: (id: number, data: any) => api.patch(`/cricket/match_players/${id}/`, data),
  delete: (id: number) => api.delete(`/cricket/match_players/${id}/`),
  getByMatch: (matchId: number) => api.get('/cricket/match_players/', { params: { match: matchId, page_size: 1000 } }),
  bulkUpdate: (data: { match_id?: number; lineup_announced?: boolean; updates: { id: number; lineup_status?: string; prev_bat_pos?: number | null }[] }) => api.post(`/cricket/match_players/bulk_update/`, data),
};

// Audit Log related API calls
export const auditLogApi = {
  getAll: (params?: PaginationParams) => api.get('/audit/activity_logs/', { params }),
  getForObject: (appLabel: string, modelName: string, objectId: number) => 
    api.get('/audit/activity_logs/object_activity/', { 
      params: { 
        content_type__app_label: appLabel,
        content_type__model: modelName.toLowerCase(),
        object_id: objectId,
        page_size: 100
      } 
    }),
};

// SquadPlayer related API calls
export const squadPlayerApi = {
  getAll: (params?: PaginationParams) => api.get('/cricket/squad_players/', { params }),
  getById: (id: number) => api.get(`/cricket/squad_players/${id}/`),
  create: (data: any) => api.post('/cricket/squad_players/', data),
  update: (id: number, data: any) => api.put(`/cricket/squad_players/${id}/`, data),
  patch: (id: number, data: any) => api.patch(`/cricket/squad_players/${id}/`, data),
  delete: (id: number) => api.delete(`/cricket/squad_players/${id}/`),
};

// Ground related API calls
export const groundApi = {
  getAll: (params?: PaginationParams) => api.get('/cricket/grounds/', { params }),
  getById: (id: number) => api.get(`/cricket/grounds/${id}/`),
  create: (data: any) => api.post('/cricket/grounds/', data),
  update: (id: number, data: any) => api.put(`/cricket/grounds/${id}/`, data),
  patch: (id: number, data: any) => api.patch(`/cricket/grounds/${id}/`, data),
  delete: (id: number) => api.delete(`/cricket/grounds/${id}/`),
  autocomplete: (params?: PaginationParams) => api.get('/cricket/grounds/autocomplete/', { params }),
};

// Team related API calls
export const teamApi = {
  getAll: (params?: PaginationParams) => api.get('/cricket/teams/', { params }),
  getById: (id: number) => api.get(`/cricket/teams/${id}/`),
  create: (data: any) => api.post('/cricket/teams/', data),
  update: (id: number, data: any) => api.put(`/cricket/teams/${id}/`, data),
  patch: (id: number, data: any) => api.patch(`/cricket/teams/${id}/`, data),
  delete: (id: number) => api.delete(`/cricket/teams/${id}/`),
};

// Authentication related API calls
export const authApi = {
  login: (credentials: { username: string; password: string }) =>
    api.post('/auth/login/', credentials),
  logout: () => api.post('/auth/logout/'),
};

// Menu V2 API endpoints
export const getMenus = async (params?: PaginationParams) => {
  const response = await api.get('/app/menu-v2/', { params });
  return response.data;
};

export const getMenu = async (id: number) => {
  const response = await api.get(`/app/menu-v2/${id}/`);
  return response.data;
};

export const getMenuSchema = async () => {
  const response = await api.get('/app/menu-v2/schema/');
  return response.data;
};

export const createMenu = async (menuData: any) => {
  const response = await api.post('/app/menu-v2/', menuData);
  return response.data;
};

export const updateMenu = async (id: number, menuData: any) => {
  const response = await api.put(`/app/menu-v2/${id}/`, menuData);
  return response.data;
};

export const deleteMenu = async (id: number) => {
  const response = await api.delete(`/app/menu-v2/${id}/`);
  return response.data;
};

// Media Folder related API calls
export const mediaFolderApi = {
  getAll: (params?: { parent?: number | null, root?: boolean | null }) => api.get('/app/folders/', { params }),
  getById: (id: number) => api.get(`/app/folders/${id}/`),
  create: (data: { name: string, parent?: { id: number | null } }) => api.post('/app/folders/', data),
  update: (id: number, data: { name: string }) => api.patch(`/app/folders/${id}/`, data),
  delete: (id: number) => api.delete(`/app/folders/${id}/`),
  getContents: (id: number, params?: { page?: number; page_size?: number; type?: string; search?: string }) => api.get(`/app/folders/${id}/contents/`, { params }),
  getRootContents: (params?: { page?: number; page_size?: number; type?: string; search?: string }) => api.get('/app/folders/root_contents/', { params }),
  getPath: (id: number) => api.get(`/app/folders/${id}/path/`),
};

// Media File related API calls
export const mediaFileApi = {
  getAll: (params?: { folder?: number | null, type?: string }) => api.get('/app/files/', { params }),
  getById: (id: number) => api.get(`/app/files/${id}/`),
  update: (id: number, data: any) => api.patch(`/app/files/${id}/`, data),
  delete: (id: number) => api.delete(`/app/files/${id}/`),
  bulkDelete: (ids: number[]) => api.post('/app/files/bulk_delete/', { ids }),
  getRootContents: () => api.get('/app/files/?folder=null'),
  upload: (formData: FormData) => {
    return api.post('/app/files/upload/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

// Media Image related API calls
export const mediaImageApi = {
  getAll: (params?: { folder?: number | null }) => api.get('/app/images/', { params }),
  getById: (id: number) => api.get(`/app/images/${id}/`),
  update: (id: number, data: any) => api.patch(`/app/images/${id}/`, data),
};

// App API endpoints
export const appApi = {
  getAll: async (params?: PaginationParams) => {
    const response = await api.get('/app/apps/', { params });
    return response.data;
  },
  getById: async (id: number) => {
    const response = await api.get(`/app/apps/${id}/`);
    return response.data;
  }
};

// Dashboard related API calls
export const dashboardApi = {
  getStats: (params?: { hours_back?: number; limit?: number }) =>
    api.get<EnhancedDashboardStats>('/stats/dashboard/stats/', { params }),
  getDailySummary: (params?: { days?: number }) =>
    api.get<DailyDashboardSummary>('/stats/dashboard/stats/daily-summary/', {
      params,
    }),
};

// User related API calls
export const userApi = {
  getUsers: (params = '') => api.get(`/user/users/${params ? '?' + params : ''}`).then(res => res.data),
  getUser: (id: string) => api.get(`/user/users/${id}/`).then(res => res.data),
  deleteUser: (id: string) => api.delete(`/user/users/${id}/`).then(res => res.data),
  getUserClients: (id: string) => api.get(`/user/users/${id}/clients/`).then(res => res.data),
  getNotifications: (id: string) => api.get(`/user/users/${id}/notifications/`).then(res => res.data),
  sendNotification: (data: any) => api.post('/user/connect/send/', data).then(res => res.data),
  getSupportDetail: (firebaseUid: string) => api.get(`/user/user-support-detail/${firebaseUid}/`).then(res => res.data),
};

interface ManualAction {
  id: number;
  action_type: string;
  key: string;
  data: string;
  done: boolean;
  cancelled: boolean;
  created_at: string;
  updated_at: string;
}

interface ManualActionResponse {
  results: ManualAction[];
  count: number;
}

export const manualActionApi = {
  getAll: async (params: { 
    page: number; 
    page_size: number; 
    search?: string;
    done?: string;
    cancelled?: string;
    action_type?: string;
  }): Promise<ManualActionResponse> => {
    const response = await api.get('/xtract/manual-actions/', { 
      params: {
        page: params.page,
        page_size: params.page_size,
        search: params.search,
        done: params.done,
        cancelled: params.cancelled,
        action_type: params.action_type
      }
    });
    return response.data;
  },
  getActionTypes: async (): Promise<string[]> => {
    const response = await api.get('/xtract/manual-actions/get_action_types/');
    return response.data;
  },
  get: async (id: number): Promise<ManualAction> => {
    const response = await api.get(`/xtract/manual-actions/${id}/`);
    return response.data;
  },
  update: async (id: number, data: Partial<ManualAction>): Promise<ManualAction> => {
    const response = await api.patch(`/xtract/manual-actions/${id}/`, data);
    return response.data;
  },
};

// MatchPlayerGroupConfiguration related API calls
export const matchPlayerGroupConfigApi = {
  getAll: (params?: PaginationParams) => api.get('/fantasy/match_player_group_config/', { params }),
  getById: (id: number) => api.get(`/fantasy/match_player_group_config/${id}/`),
  create: (data: any) => api.post('/fantasy/match_player_group_config/', data),
  update: (id: number, data: any) => api.put(`/fantasy/match_player_group_config/${id}/`, data),
  patch: (id: number, data: any) => api.patch(`/fantasy/match_player_group_config/${id}/`, data),
  delete: (id: number) => api.delete(`/fantasy/match_player_group_config/${id}/`),
  getByMatch: (matchId: number) => api.get(`/fantasy/match_player_group_config/by_match/`, { params: { match_id: matchId } }),
};

// Sport Events related API calls
export const sportEventApi = {
  getAll: (params?: { 
    platform?: string;
    sport?: string;
    start_time_after?: string;
    start_time_before?: string;
    page_size?: number;
    is_second_inning?: boolean;
  }) => api.get('/xtract/sport-events/', { params }),
  getById: (id: number) => api.get(`/xtract/sport-events/${id}/`),
  create: (data: any) => api.post('/xtract/sport-events/', data),
  update: (id: number, data: any) => api.put(`/xtract/sport-events/${id}/`, data),
  patch: (id: number, data: any) => api.patch(`/xtract/sport-events/${id}/`, data),
  delete: (id: number) => api.delete(`/xtract/sport-events/${id}/`),
};

export const tournamentApi = {
  getAll: (params?: any) => api.get('/xtract/tournaments/', { params }),
  getById: (id: number) => api.get(`/xtract/tournaments/${id}/`),
  getActive: (selectedSport: string) => api.get('/xtract/tournaments/get_active/', { params: { sport: selectedSport } }),
  getUpcoming: (selectedSport: string) => api.get('/xtract/tournaments/get_upcoming/', { params: { sport: selectedSport } }),
  getRecent: (selectedSport: string) => api.get('/xtract/tournaments/get_recent/', { params: { sport: selectedSport } }),
  fetchEspnById: (object_id: number) => api.post('/xtract/tournaments/fetch_espn_tournament_by_id/', { object_id }),
};

// Article API
export const articleApi = {
  list: (params?: ArticleListParams) => 
    api.get<ArticleListResponse>('/cms/articles/', { params }),
  getById: (id: number) => 
    api.get<Article>(`/cms/articles/${id}/`),
  create: (data: Partial<Article>) => 
    api.post<Article>('/cms/articles/', data),
  update: (id: number, data: Partial<Article>) => 
    api.put<Article>(`/cms/articles/${id}/`, data),
  delete: (id: number) => 
    api.delete(`/cms/articles/${id}/`),
  createDraft: (id: number) => 
    api.post<Article>(`/cms/articles/${id}/create_draft/`),
  publish: (id: number) => 
    api.post<Article>(`/cms/articles/${id}/publish/`),
  discardDraft: (id: number) => 
    api.delete(`/cms/articles/${id}/discard_draft/`),
  schedulePublish: (id: number, data: { scheduled_publish_at: string | null; auto_publish_enabled: boolean }) => 
    api.post<Article>(`/cms/articles/${id}/schedule_publish/`, data),
  setupSocialSharing: (id: number, data: { social_sharing: any }) => 
    api.post<Article>(`/cms/articles/${id}/setup_social_sharing/`, data),
  shareOnSocial: (id: number, data: { platforms: string[] }) => 
    api.post<{message: string; article: Article}>(`/cms/articles/${id}/share_on_social/`, data),
  addTag: (id: number, data: { tag_type: string; content_type: string; object_id: number }) =>
    api.post<Article>(`/cms/articles/${id}/add_tag/`, data),
  removeTag: (id: number, data: { tag_id: number }) =>
    api.post<Article>(`/cms/articles/${id}/remove_tag/`, data),
  getTags: (id: number) =>
    api.get<{ tags: TaggedItem[] }>(`/cms/articles/${id}/tags/`),
  unpublish: (id: number) => 
    api.post<Article>(`/cms/articles/${id}/unpublish/`),
  // Writes the top-of-page summary in English and Hindi (one model call) from the body; nothing is saved until the editor saves.
  generateSummary: (id: number, data: { content_markdown?: string; title?: string }) =>
    api.post<{ summary_en: string; summary_hi: string }>(`/cms/articles/${id}/generate_summary/`, data),
  sendToTelegram: (id: number) =>
    api.post<{ message: string; article?: Article }>(`/cms/articles/${id}/send_to_telegram/`),
  sendToTestTelegram: (id: number) =>
    api.post<{ message: string; article?: Article }>(`/cms/articles/${id}/send_to_telegram_test/`),
  requestIndexing: (id: number) =>
    api.post<{ message: string }>(`/cms/articles/${id}/request_indexing/`),
  requestIndexNow: (id: number) =>
    api.post<{ message: string }>(`/cms/articles/${id}/request_indexnow/`),
  checkGoogleIndex: (id: number) =>
    api.post<{ message: string; google_index_status: GoogleIndexStatus }>(`/cms/articles/${id}/check_google_index/`),
  taggedByMatches: (matchIds: number[]) =>
    api
      .get<Record<string, { id: number; title: string; slug: string; is_draft: boolean; published: boolean }[]>>(
        '/cms/articles/tagged-matches/',
        { params: { match_ids: matchIds } }
      )
      .then((r) => r.data),
};

// FAQ API
export const faqApi = {
  list: () => api.get('/cms/faqs/'),
  getById: (id: number) => api.get(`/cms/faqs/${id}/`),
  create: (data: any) => api.post('/cms/faqs/', data),
  update: (id: number, data: any) => api.put(`/cms/faqs/${id}/`, data),
  delete: (id: number) => api.delete(`/cms/faqs/${id}/`),
  publish: (id: number) => api.post(`/cms/faqs/${id}/publish/`),
};

// Page API
export const pageApi = {
  list: (params?: PaginationParams) => api.get('/cms/pages/', { params }),
  getById: (id: number) => api.get(`/cms/pages/${id}/`),
  create: (data: any) => api.post('/cms/pages/', data),
  update: (id: number, data: any) => api.put(`/cms/pages/${id}/`, data),
  delete: (id: number) => api.delete(`/cms/pages/${id}/`),
  createDraft: (id: number) => api.post(`/cms/pages/${id}/create_draft/`),
  publish: (id: number) => api.post(`/cms/pages/${id}/publish/`),
  discardDraft: (id: number) => api.delete(`/cms/pages/${id}/discard_draft/`),
  unpublish: (id: number) => api.post(`/cms/pages/${id}/unpublish/`),
  schemas: () => api.get('/cms/pages/schemas/'),
  bulkUpdateHierarchy: (updates: {id: number, parent: number|null}[]) => api.post('/cms/pages/bulk_update_hierarchy/', updates),
};

// USP Card API
export const uspCardApi = {
  list: () => api.get('/cms/usp-cards/'),
  getById: (id: number) => api.get(`/cms/usp-cards/${id}/`),
  create: (data: any) => api.post('/cms/usp-cards/', data),
  update: (id: number, data: any) => api.put(`/cms/usp-cards/${id}/`, data),
  delete: (id: number) => api.delete(`/cms/usp-cards/${id}/`),
  reorder: (data: { id: number; order: number }[]) => api.post('/cms/usp-cards/reorder/', data),
};

// Template API
export const templateApi = {
  list: () => api.get('/cms/templates/'),
  getById: (id: number) => api.get(`/cms/templates/${id}/`),
  create: (data: any) => api.post('/cms/templates/', data),
  update: (id: number, data: any) => api.put(`/cms/templates/${id}/`, data),
  delete: (id: number) => api.delete(`/cms/templates/${id}/`),
};

// Author API
export const authorApi = {
  list: (params?: PaginationParams) => api.get('/cms/authors/', { params }),
  getById: (id: number) => api.get(`/cms/authors/${id}/`),
  create: (data: any) => api.post('/cms/authors/', data),
  update: (id: number, data: any) => api.patch(`/cms/authors/${id}/`, data),
  delete: (id: number) => api.delete(`/cms/authors/${id}/`),
};

// SeriesPoints related API calls
export const seriesPointsApi = {
  getAll: (params?: PaginationParams) => api.get('/cricket/series_points/', { params }),
};

// Support Quick Reply API
export interface SupportQuickReply {
  id: string;
  shortcut: string;
  title: string;
  message: string;
  category: string;
  is_active: boolean;
  order: number;
  created_by: string | null;
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
}

export const supportQuickReplyApi = {
  list: (params?: { is_active?: boolean; search?: string; category?: string }) =>
    api.get<SupportQuickReply[]>('/connect/quick-replies/', { params }).then(r => r.data),
  create: (data: Partial<SupportQuickReply>) =>
    api.post<SupportQuickReply>('/connect/quick-replies/', data).then(r => r.data),
  update: (id: string, data: Partial<SupportQuickReply>) =>
    api.patch<SupportQuickReply>(`/connect/quick-replies/${id}/`, data).then(r => r.data),
  delete: (id: string) =>
    api.delete(`/connect/quick-replies/${id}/`).then(r => r.data),
};

// Dream Team Analysis API
export const dreamTeamAnalysisApi = {
  getAll: (params?: PaginationParams) =>
    api.get('/cricket/dream_team_analysis/', { params }),
  getById: (id: number) =>
    api.get(`/cricket/dream_team_analysis/${id}/`),
  update: (id: number, data: any) =>
    api.patch(`/cricket/dream_team_analysis/${id}/`, data),
  run: (data: {
    scope: 'SERIES' | 'FORMAT';
    series_id?: number;
    match_format?: string;
    gender?: string;
    ground_id?: number;
    cutoff?: string;
  }) => api.post('/cricket/dream_team_analysis/run/', data),
  getBySeries: (seriesId: number) =>
    api.get('/cricket/dream_team_analysis/by_series/', { params: { series_id: seriesId } }),
};

export default api;